import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Button, ClearableInput } from '@rio-cloud/rio-uikit';
import { Failure, FailureFormData } from '../../../../models/failure';

interface FailureFormProps {
  failure?: Failure | null;
  onSave: (title: string, description: string, videoFile?: File) => Promise<boolean>;
  onCancel: () => void;
}

const FailureForm: React.FC<FailureFormProps> = ({
  failure,
  onSave,
  onCancel,
}) => {
  const intl = useIntl();
  const [formData, setFormData] = useState({
    id: 0,
    title: '',
    description: '',
    videoUrl: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [existingVideoRemoved, setExistingVideoRemoved] = useState(false);

  useEffect(() => {
    if (failure) {
      setFormData({
        id: failure.id || 0,
        title: failure.title,
        description: failure.description,
        videoUrl: failure.videoUrl,
      });
      setExistingVideoRemoved(false);
    }
  }, [failure]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.titleRequired' });
    }

    if (!formData.description.trim()) {
      newErrors.description = intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.descriptionRequired' });
    }

    // Para criação, arquivo é obrigatório. Para edição, pode manter o existente
    if (!failure && !videoFile) {
      newErrors.video = intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.videoRequired' });
    }

    // Para edição, se removeu o vídeo existente, precisa selecionar um novo
    if (failure && existingVideoRemoved && !videoFile) {
      newErrors.video = intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.videoRequired' });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Para criação, sempre envia o arquivo
      // Para edição, só envia se selecionou um novo arquivo
      const fileToSend = videoFile || undefined;
      const success = await onSave(formData.title, formData.description, fileToSend);
      if (success) {
        onCancel();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('video/')) {
        setVideoFile(file);
        setExistingVideoRemoved(false);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type.startsWith('video/')) {
        setVideoFile(file);
        setExistingVideoRemoved(false);
      }
    }
  };

  const removeVideoFile = () => {
    setVideoFile(null);
    if (failure && formData.videoUrl) {
      setExistingVideoRemoved(true);
    }
  };

  const hasExistingVideo = failure && formData.videoUrl && !existingVideoRemoved;
  const hasNewVideo = videoFile !== null;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            {intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.titleLabel' })}
          </label>
          <ClearableInput
            value={formData.title}
            onChange={(value) => setFormData(prev => ({ ...prev, title: value }))}
            placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.titlePlaceholder' })}
          />
          {errors.title && (
            <p className="mt-1 text-red-600">{errors.title}</p>
          )}
        </div>

        <div>
          <label className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            {intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.descriptionLabel' })}
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.descriptionPlaceholder' })}
            className={`w-full px-3 py-2 border rounded-md dark:bg-neutral-800 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.description ? 'border-red-500' : 'border-gray-300 dark:border-neutral-700'
              }`}
            rows={4}
          />
          {errors.description && (
            <p className="mt-1 text-red-600">{errors.description}</p>
          )}
        </div>

        <div>
          <label className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            {intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.videoLabel' })} {!failure ? '*' : ''}
          </label>

          {hasNewVideo ? (
            // Novo arquivo selecionado
            <div className="border border-gray-300 dark:border-neutral-700 rounded-md p-4 bg-gray-50 dark:bg-neutral-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="rioglyph rioglyph-video text-blue-600 text-lg" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-neutral-100">{videoFile!.name}</p>
                    <p className="text-xs text-gray-500 dark:text-neutral-400">
                      {(videoFile!.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Button
                  bsStyle={Button.DANGER}
                  onClick={removeVideoFile}
                  iconName="rioglyph-remove"
                >
                  {intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.remove' })}
                </Button>
              </div>
            </div>
          ) : hasExistingVideo ? (
            // Vídeo existente (apenas na edição)
            <div className="border border-gray-300 dark:border-neutral-700 rounded-md p-4 bg-gray-50 dark:bg-neutral-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="rioglyph rioglyph-video text-green-600 text-lg" />
                  <div>
                    <span
                      onClick={() => {
                        window.open(formData.videoUrl, '_blank');
                      }}
                      className="text-color-primary cursor-pointer"
                    >
                      {intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.failureVideo' })}
                    </span>
                  </div>
                </div>
                <Button
                  bsStyle={Button.DANGER}
                  onClick={removeVideoFile}
                  iconName="rioglyph-remove"
                >
                  {intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.remove' })}
                </Button>
              </div>
            </div>
          ) : (
            // Área de upload
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center ${dragActive ? 'border-blue-400 bg-blue-50 dark:bg-blue-950' : 'border-gray-300 dark:border-neutral-700'
                }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div className="space-y-4">
                <span className="rioglyph rioglyph-cloud-upload text-4xl text-gray-400 dark:text-neutral-500" />
                <div>
                  <p className="text-gray-600 dark:text-neutral-400">
                    {intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.dropHint' })}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-neutral-400 mt-1">
                    {intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.formats' })}
                  </p>
                </div>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="video-upload"
                />
                <label
                  htmlFor="video-upload"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-neutral-700 rounded-md shadow-sm font-medium text-gray-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-700 cursor-pointer"
                >
                  {intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.selectFile' })}
                </label>
              </div>
            </div>
          )}

          {errors.video && (
            <p className="mt-1 text-red-600">{errors.video}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-6">
        <Button
          bsStyle={Button.DEFAULT}
          onClick={onCancel}
          disabled={loading}
        >
          {intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.cancel' })}
        </Button>
        <Button
          bsStyle={Button.PRIMARY}
          type="submit"
          disabled={loading}
          iconName={loading ? "rioglyph-spinner" : undefined}
        >
          {loading ? intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.saving' }) : intl.formatMessage({ id: 'intl-msg:backoffice.failureForm.save' })}
        </Button>
      </div>
    </form>
  );
};

export default FailureForm;
